import React, { useContext, useEffect, useState } from "react";
import api from "../../utils/api";
import Icon from "../common/icons";
import { fetchMyTests } from "./useMyTests";
import { UserContext } from "../../context/UserContext";
import { buildHomeCards, launcherHint, loadHomeData, showsPrograms } from "./homeCardsData";
import chatbotAvatar from "../../assets/Anna_Chat_Avatar.png";

// The closed-chat launcher: a yellow pill that says what to do next. Mid-test it reads
// "Continue RIASEC · Question 12 of 42" with a progress ring round Anna; with tests still
// to take, "Take a test · 4 tests ready"; otherwise "Chat with Anna". Styles: "Chat
// launcher" in styles/chatbot.css (cl- classes). It keeps the .chathead class because the
// landing tour opens the chat by clicking .chathead.

// The names students see in the chat ("Continue Mood and stress · Question 13 of 42")
const NAMES = { riasec: "Career interests", dass42: "Mood and stress", ryff: "Well-being", sdq: "Strengths and difficulties" };
const RING = 2 * Math.PI * 21; // ring circumference: r = 21 in a 48px box

/** What the pill says, from the open sessions, the assigned tests and the starts left per test. */
const describe = (progress, assigned, starts) => {
  const open = Object.entries(progress || {})
    .filter(([, p]) => p && p.status === "in_progress" && p.total > 0 && p.current < p.total)
    .sort(([, a], [, b]) => String(b.updated_at || "").localeCompare(String(a.updated_at || "")));
  if (open.length) {
    const [type, p] = open[0];
    return { kind: "continue", type, current: p.current, total: p.total };
  }

  let left = starts;
  if (typeof left === "string") {
    try {
      left = JSON.parse(left);
    } catch {
      left = null;
    }
  }
  const tests = Array.isArray(assigned) ? assigned : [];
  const ready =
    left && typeof left === "object"
      ? tests.filter((type) => Number(left[type]) > 0).length
      : typeof left === "number" && left > 0
        ? tests.length
        : 0;
  return ready > 0 ? { kind: "ready", count: ready } : { kind: "chat" };
};

/**
 * The same from the server's list (useMyTests.js): only tests that can really be taken count as
 * ready, so a closed test never shows as "2 tests ready".
 */
export const describeTests = (tests) => {
  const list = Array.isArray(tests) ? tests : [];
  const open = list
    .filter((test) => test.status === "in_progress" && Number(test.total) > 0 && Number(test.answered) > 0)
    .sort((a, b) => String(b.updated_at || "").localeCompare(String(a.updated_at || "")));
  if (open.length) {
    const test = open[0];
    const total = Number(test.total);
    const current = Math.min(Number(test.answered) || 0, total);
    return current >= total
      ? { kind: "finish", type: test.type, current, total }
      : { kind: "continue", type: test.type, current, total };
  }
  const ready = list.filter((test) => test.status === "open" || test.status === "in_progress").length;
  return ready > 0 ? { kind: "ready", count: ready } : { kind: "chat" };
};

const ChatLauncher = ({ language, onOpen }) => {
  const fil = language === "fil";
  const [state, setState] = useState(null);
  const [heroInView, setHeroInView] = useState(false);

  // On the landing page the hero has its own "Chat with Anna" button. On phones the pill
  // would sit on top of it, so it tucks away while that button is on screen (CSS: is-tucked).
  useEffect(() => {
    const heroActions = document.querySelector(".lp-hero-actions");
    if (!heroActions || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(([entry]) => setHeroInView(entry.isIntersecting), {
      threshold: 0.4,
    });
    observer.observe(heroActions);
    return () => observer.disconnect();
  }, []);

  // Read fresh each time the chat closes (this mounts then), so the label follows progress.
  useEffect(() => {
    let alive = true;
    // A slow network shouldn't hide the way into the chat
    const fallback = setTimeout(() => {
      if (alive) setState((current) => current || { kind: "chat" });
    }, 2500);
    const legacy = () => Promise.allSettled([
      api.get("/api/get-assessment-progress/"),
      api.get("/api/check_login_status/"),
      api.get("/api/check-max-chatbot-starts/"),
    ]).then(([progress, login, starts]) => {
      if (!alive) return;
      const value = (result) => (result.status === "fulfilled" ? result.value.data : null);
      setState(
        describe(
          value(progress)?.assessment_progress,
          value(login)?.user_data?.assigned_assessments,
          value(starts)?.max_chatbot_starts,
        ),
      );
    });
    // The server's list first (useMyTests.js); the older checks only if it can't be read
    fetchMyTests({ fresh: true })
      .then((tests) => {
        if (!alive) return;
        if (Array.isArray(tests)) setState(describeTests(tests));
        else setState((current) => current || { kind: "chat" });
      })
      .catch(() => (alive ? legacy() : null));
    return () => {
      alive = false;
      clearTimeout(fallback);
    };
  }, []);

  // N4: with no test to take or continue, the pill points at the chat's most useful home card
  // (a result ready, the next session, saved programs). Asked only in that case.
  const { user } = useContext(UserContext) || {};
  const [hint, setHint] = useState(null);
  const isChat = state?.kind === "chat";
  useEffect(() => {
    if (!isChat) return undefined;
    let alive = true;
    const programs = showsPrograms(user);
    loadHomeData({ programs })
      .then((data) => {
        if (!alive) return;
        const [top] = buildHomeCards({ data, userId: user?.id, programs, lang: language });
        setHint(launcherHint(top, language));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [isChat, user?.id, language]);

  if (!state) return null;

  let title;
  let subtitle = null;
  let ratio = null;
  if (state.kind === "continue") {
    const name = NAMES[state.type] || state.type;
    title = fil ? `Ituloy ang ${name}` : `Continue ${name}`;
    subtitle = fil
      ? `Tanong ${state.current + 1} sa ${state.total}`
      : `Question ${state.current + 1} of ${state.total}`;
    ratio = state.current / state.total;
  } else if (state.kind === "finish") {
    // Every answer saved, only the sending left (F7)
    const name = NAMES[state.type] || state.type;
    title = fil ? `Tapusin ang ${name}` : `Finish ${name}`;
    subtitle = fil ? `Nasagot na ang lahat ng ${state.total}` : `All ${state.total} answered`;
    ratio = 1;
  } else if (state.kind === "ready") {
    title = fil ? "Kumuha ng test" : "Take a test";
    subtitle = fil
      ? `${state.count} na test ang handa`
      : `${state.count} ${state.count === 1 ? "test" : "tests"} ready`;
  } else if (hint) {
    ({ title, subtitle } = hint);
  } else {
    title = fil ? "Makipag-chat kay Anna" : "Chat with Anna";
  }

  return (
    <div className={`cl-launcher${heroInView ? " is-tucked" : ""}`}>
      <button
        type="button"
        className="cl-pill chathead"
        onClick={onOpen}
        aria-label={subtitle ? `${title}, ${subtitle}` : title}
      >
        <span className="cl-avatar">
          {ratio !== null && (
            <svg className="cl-ring" viewBox="0 0 48 48" aria-hidden="true">
              <circle className="cl-ring-track" cx="24" cy="24" r="21" />
              <circle
                className="cl-ring-fill"
                cx="24"
                cy="24"
                r="21"
                strokeDasharray={RING}
                strokeDashoffset={RING * (1 - ratio)}
              />
            </svg>
          )}
          <img src={chatbotAvatar} alt="" />
        </span>
        <span className="cl-text">
          <span className="cl-title">{title}</span>
          {subtitle && <span className="cl-sub">{subtitle}</span>}
        </span>
        <Icon name="arrow-right" size={18} className="cl-arrow" />
      </button>
    </div>
  );
};

export default ChatLauncher;
